import {
  type Alert,
  type Client,
  type ClientDocument,
  type CompanyWorkspace,
  can,
  computeAlerts,
  computeNoticeObligations,
  computeThresholds,
  computeZeroReports,
  type IsoMonth,
  type Me,
  type NoticeObligation,
  noticesByOperation,
  type Permission,
  pendingZeroReports,
  type Thresholds,
  today,
  type ZeroReport,
  zeroReportStartMonth,
} from "@pcj/shared";
import { CalendarClock, Coins, FileText, History, type LucideIcon, Settings, ShieldAlert, Users } from "lucide-react";
import { useMemo } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useOutletContext, useParams } from "react-router";
import { AppHeader } from "../../components/AppHeader";
import { Banner, EmptyState, PageLoader } from "../../components/ui";
import { ApiError, errorMessage } from "../../lib/api";
import { useCompanyActions, useWorkspace } from "../../lib/queries";
import { useCurrentUser } from "../SessionPages";

/** Everything a tab needs: raw data, derived compliance state, permissions, actions. */
export interface CompanyState extends CompanyWorkspace {
  me: Me;
  companyId: string;
  clientById: Map<string, Client>;
  thresholds: Thresholds;
  notices: NoticeObligation[];
  noticeIndex: Map<string, NoticeObligation[]>;
  alerts: Alert[];
  zeroReports: ZeroReport[];
  /** First month of the company's records (registration, or its oldest operation). */
  startMonth: IsoMonth;
  documentsByClient: Map<string, ClientDocument[]>;
  /** The server can store client documents. */
  documentsEnabled: boolean;
  today: string;
  can: (permission: Permission) => boolean;
  /** The chamber admin sees everything but changes nothing. */
  readOnly: boolean;
  actions: ReturnType<typeof useCompanyActions>;
}

export const useCompany = () => useOutletContext<CompanyState>();

function groupByClient(documents: readonly ClientDocument[]) {
  const byClient = new Map<string, ClientDocument[]>();
  for (const document of documents) {
    const list = byClient.get(document.clientId);
    if (list) list.push(document);
    else byClient.set(document.clientId, [document]);
  }
  return byClient;
}

/** All compliance figures are recomputed from the raw data with the shared rules. */
function derive(workspace: CompanyWorkspace) {
  const day = today();
  const thresholds = computeThresholds(workspace.company.umaValue);
  const notices = computeNoticeObligations(workspace.operations, thresholds);
  const startMonth = zeroReportStartMonth(workspace.company.createdAt, workspace.operations);
  return {
    today: day,
    thresholds,
    notices,
    startMonth,
    noticeIndex: noticesByOperation(notices),
    clientById: new Map(workspace.clients.map((c) => [c.id, c])),
    documentsByClient: groupByClient(workspace.documents),
    alerts: computeAlerts({
      clients: workspace.clients,
      operations: workspace.operations,
      thresholds,
      notices,
      today: day,
      dismissedIds: workspace.dismissedAlerts.map((d) => d.alertId),
    }),
    zeroReports: computeZeroReports({ startMonth, today: day, notices, filed: workspace.company.zeroReportsFiled }),
  };
}

interface Tab {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

export function CompanyLayout() {
  const { companyId = "" } = useParams();
  const me = useCurrentUser();
  const location = useLocation();
  const workspace = useWorkspace(companyId);
  const actions = useCompanyActions(companyId);
  const derived = useMemo(() => (workspace.data ? derive(workspace.data) : null), [workspace.data]);
  const isChamber = me.profile.role === "chamber_admin";
  const backTo = isChamber ? "/chamber" : undefined;

  if (workspace.isPending || !derived) {
    if (workspace.isError) {
      const missing = workspace.error instanceof ApiError && workspace.error.status === 404;
      return (
        <div className="min-h-screen">
          <AppHeader me={me} title="Empresa" backTo={backTo} />
          <main className="mx-auto max-w-xl px-4 py-10">
            {missing ? (
              <EmptyState icon={ShieldAlert} title="No encontramos esa empresa">
                <Link to="/" className="underline">
                  Volver al inicio
                </Link>
              </EmptyState>
            ) : (
              <Banner tone="error">{errorMessage(workspace.error)}</Banner>
            )}
          </main>
        </div>
      );
    }
    return (
      <div className="min-h-screen">
        <AppHeader me={me} title="Cargando…" backTo={backTo} />
        <PageLoader label="Cargando expediente…" />
      </div>
    );
  }

  const data = workspace.data as CompanyWorkspace;
  const role = me.profile.role;
  const state: CompanyState = {
    ...data,
    ...derived,
    me,
    companyId,
    documentsEnabled: me.features.documents,
    can: (permission) => can(role, permission),
    readOnly: isChamber,
    actions,
  };

  const complianceTabs = role !== "operator";
  const tabs: Tab[] = [
    { to: "clients", label: "Clientes", icon: Users },
    { to: "operations", label: "Operaciones", icon: FileText },
    ...(complianceTabs
      ? [
          { to: "alerts", label: "Riesgo y alertas", icon: ShieldAlert, badge: derived.alerts.length },
          {
            to: "reports",
            label: "Avisos e informes",
            icon: CalendarClock,
            badge: derived.notices.filter((n) => !n.filed).length + pendingZeroReports(derived.zeroReports).length,
          },
          { to: "cash", label: "Efectivo · Art. 32", icon: Coins },
          ...(can(role, "viewAuditLog") ? [{ to: "audit-log", label: "Bitácora", icon: History }] : []),
          { to: "settings", label: "Configuración", icon: Settings },
        ]
      : []),
  ];

  // Operators only see capture tabs; deep links to other tabs fall back to clients.
  const current = location.pathname.split("/")[3];
  if (current && !tabs.some((t) => t.to === current)) return <Navigate to="clients" replace />;

  return (
    <div className="min-h-screen">
      <AppHeader me={me} title={data.company.legalName} backTo={backTo} />
      {state.readOnly && (
        <div className="bg-amber-50 px-4 py-2 text-center text-xs text-amber-800">
          Estás viendo esta empresa en modo solo lectura, como administrador de la Cámara. Lo único que puedes cambiar
          es el acceso de su equipo, en Configuración.
        </div>
      )}
      <nav className="border-b border-gray-200 bg-white px-4 sm:px-8" aria-label="Secciones">
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                `relative flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition ${
                  isActive ? "border-gold-700 text-charcoal" : "border-transparent text-gray-400 hover:text-gray-700"
                }`
              }
            >
              <tab.icon className="h-4 w-4" aria-hidden />
              {tab.label}
              {!!tab.badge && (
                <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">
                  {tab.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-8">
        <Outlet context={state} />
      </main>
    </div>
  );
}
